import { useState } from 'react';

import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { Form } from '#/components/Form/Form';
import type { FormSubmitFunctionData } from '#/components/Form/FormContext';
import { useAppConfig } from '#/config/AppConfigContext';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { CmsApiSupportContactFormBlockData } from '#/modules/cms/api/types/blocks/CmsApiSupportContactFormBlock';
import { postJson } from '#/utils/http/postJson';
import { resetRecaptcha } from '#/utils/recaptcha';

import {
  Container,
  Content,
  Headline,
  HeadlineBody,
  NoteSection,
  Panel,
} from './CmsSupportContactFormBlock.styled';
import { CmsSupportContactFormBlockContactForm } from './CmsSupportContactFormBlockContactForm';
import { CmsSupportContactFormBlockSummary } from './CmsSupportContactFormBlockSummary';

type FormValues = {
  email?: string;
  firstName?: string;
  lastName?: string;
  locale?: I18nLocale;
  reason?: string;
  subReason?: string;
  subSubReason?: string;
  orgName?: string;
  message?: string;
  attachmentIds: (string | null | undefined)[];
  gRecaptchaResponse?: string;
  recaptchaKey: number;
};

const handleSubmit = (
  { handleError, updateSubmitting, values }: FormSubmitFunctionData<FormValues>,
  blockId: string,
  user: ApiUser | undefined,
  setSubmittedValues: (values: FormValues | null | undefined) => void,
) => {
  const { orgs = [] } = user || {};
  const { id: orgId = '' } =
    orgs.find((org) => org.name === values.orgName) ?? {};
  const valuesToSubmit = {
    orgId,
    ...values,
    attachmentIds: (values.attachmentIds ?? []).filter(
      (attachment): attachment is string => Boolean(attachment),
    ),
  };
  postJson(`/data/website/support-contact-form/${blockId}`, valuesToSubmit)
    .then(() => {
      updateSubmitting(false);
      setSubmittedValues(valuesToSubmit);
    })
    .catch((e) => {
      resetRecaptcha();
      handleError(e, {
        recaptchaKey: values.recaptchaKey + 1,
        gRecaptchaResponse: undefined,
      });
    });
};

type Props = {
  data: CmsApiSupportContactFormBlockData;
  isPageHeader?: boolean;
  blockId: string;
};

export function CmsSupportContactFormBlock({
  blockId,
  data,
  isPageHeader,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const { user } = useUser();
  const initialValues: FormValues = {
    email: user?.email,
    firstName: user?.firstName,
    lastName: user?.lastName,
    locale,
    reason: '',
    subReason: '',
    subSubReason: '',
    orgName: '',
    message: '',
    attachmentIds: [''],
    gRecaptchaResponse: undefined,
    recaptchaKey: 0,
  };
  const [submittedValues, setSubmittedValues] = useState<
    FormValues | null | undefined
  >();
  const { reasons } = data;
  const hasSubmittedWithFeedback = submittedValues?.reason === 'FEEDBACK';

  if (submittedValues)
    return (
      <CmsSupportContactFormBlockSummary
        data={data}
        hasSubmittedWithFeedback={Boolean(hasSubmittedWithFeedback)}
      />
    );

  return (
    <Container
      $imageHandle={data.bgImage?.handle}
      $mimetype={data.bgImage?.mimetype}
      $appConfig={appConfig}
    >
      <Content>
        <Panel>
          <Headline
            isPageHeader={Boolean(isPageHeader)}
            text={data.headline ?? 'Contact a Real Person at Idealist'}
          />
          {data.body ? (
            <HeadlineBody dangerouslySetInnerHTML={{ __html: data.body }} />
          ) : (
            <HeadlineBody>
              <p>
                Please fill out the contact form below. Alternatively, you can
                send us an email at{' '}
                <a href="mailto:support@idealist.org">support@idealist.org</a>{' '}
                or give us a call at{' '}
                <a href="tel:+1646-786-6886">646-786-6886</a>.
              </p>
            </HeadlineBody>
          )}
          {data.availability ? (
            <NoteSection
              label={i18n('Availability:')}
              text={data.availability}
            />
          ) : null}
          {data.address ? (
            <NoteSection label={i18n('Address:')} text={data.address} />
          ) : null}
          <Form
            analyticsTitle="[cms] Support contact block"
            usageContext="page"
            qaId="support-contact-form"
            initialValues={initialValues}
            onSubmit={(formContext) =>
              handleSubmit(
                formContext,
                blockId,
                user ?? undefined,
                setSubmittedValues,
              )
            }
            render={({ getValue, updateValue }) => {
              const selectedReason = reasons.find(
                (r) => r.id === getValue('reason'),
              );
              const selectedSubReason = selectedReason?.subReasons?.find(
                (s) => s.id === getValue('subReason'),
              );
              return (
                <CmsSupportContactFormBlockContactForm
                  reasons={reasons}
                  selectedReason={selectedReason}
                  selectedSubReason={selectedSubReason}
                  updateValue={updateValue}
                />
              );
            }}
          />
        </Panel>
      </Content>
    </Container>
  );
}
